import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Paper, Typography, CircularProgress, Alert, useTheme } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { fetchCancellationOverTime } from "../../../actions/userCancellationActions";

/**
 * Rolling 12-month YoY view of total cancellations. Two lines overlaid:
 *  - This year (last 12 calendar months ending with the current month)
 *  - Same months one year prior
 *
 * Independent of the page-level period selector — the bar chart + KPI
 * cards answer "what's happening in the chosen window"; this chart
 * answers "are we trending up or down vs last year, regardless of the
 * window choice." Buckets that fall before the feature shipped come back
 * with count=0 from the API; the line just sits on the x-axis there
 * (Lucas's "no data" treatment).
 */
const CancellationOverTimeChart = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { loading, data, error } = useSelector(
    (state) => state.user_cancellations.overTime
  );

  useEffect(() => {
    dispatch(fetchCancellationOverTime());
  }, [dispatch]);

  const chartData = useMemo(() => {
    if (!data?.buckets) return null;
    // X labels: short month names. Year prefix only on January boundaries
    // to avoid "2025 Jan 2025 Feb..." clutter while still showing the
    // year flip on the x-axis.
    const xLabels = data.buckets.map((b) => {
      const [year, month] = b.month.split("-");
      const dt = new Date(Number(year), Number(month) - 1, 1);
      const monthName = dt.toLocaleString(undefined, { month: "short" });
      return Number(month) === 1 ? `${monthName} ${year}` : monthName;
    });
    return {
      xLabels,
      currentSeries: data.buckets.map((b) => b.count),
      previousSeries: data.buckets.map((b) => b.previous_year_count),
    };
  }, [data]);

  if (loading && !data) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Cancellations Over Time (Last 12 Months)</Typography>
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }
  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Cancellations Over Time (Last 12 Months)</Typography>
        <Alert severity="error">Couldn't load the time-series data.</Alert>
      </Paper>
    );
  }
  if (!chartData) return null;

  const allZero = chartData.currentSeries.every((v) => v === 0) && chartData.previousSeries.every((v) => v === 0);
  if (allZero) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Cancellations Over Time (Last 12 Months)</Typography>
        <Alert severity="info">No cancellation data in the last 24 months.</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Cancellations Over Time (Last 12 Months)</Typography>
        <Typography variant="body2" color="text.secondary">
          Total cancellations per month, overlaid with the same months one year prior.
        </Typography>
      </Box>
      <LineChart
        xAxis={[{ scaleType: "point", data: chartData.xLabels }]}
        series={[
          {
            data: chartData.currentSeries,
            label: "This year",
            color: theme.palette.primary.main,
            curve: "monotoneX",
            showMark: true,
            valueFormatter: (v) => (v == null ? "—" : `${v} cancellation${v === 1 ? "" : "s"}`),
          },
          {
            data: chartData.previousSeries,
            label: "Year prior",
            color: theme.palette.mode === "dark" ? theme.palette.grey[500] : theme.palette.grey[400],
            curve: "monotoneX",
            showMark: true,
            valueFormatter: (v) => (v == null ? "—" : `${v} cancellation${v === 1 ? "" : "s"}`),
          },
        ]}
        height={320}
        margin={{ left: 60, right: 20, top: 20, bottom: 50 }}
      />
    </Paper>
  );
};

export default CancellationOverTimeChart;
